import {put, call, all} from 'redux-saga/effects';
import Group from "../../entities/group";
import * as Actions from "../actions/groupActions";
import * as idb from '../../lib/idbd';
import * as api from '../../rest/api';
import {processQueue} from "../../queues/processQueue";
import newHRTimestamp from "../../lib/picos/timestamp";
import {ensureOperationId} from '../../lib/pendingRows';
import {tabSession} from '../../Auth/session';

// Inlined to reduce circular dependencies
function getCurrentUserId() {
    return sessionStorage.getItem('userId');
}

// Inlined to reduce circular dependencies
function getCurrentFirmId() {
    return sessionStorage.getItem('firmId');
}

type GroupAction = {
    groupId: string;
    group: Group;
    groups: Group[];
    sentAddedOn?: number;
    onQueued?: (saved: boolean) => void;
};

export function* fetchOneGroup(action: GroupAction) {
	const session = tabSession.captureGeneration();
	try {
		const group: Group = yield call(api.findOne, 'group', action.groupId);
		session.assertCurrent();

		// Now we can update the user interface
		yield put({type: Actions.GROUP_FETCH_ONE_SUCCESS, groupId: action.groupId, group, sentAddedOn: action.sentAddedOn});
	}
	catch(err) {
		if (session.signal.aborted) return;
		yield put({type: Actions.GROUP_FETCH_ONE_FAILURE, groupId: action.groupId, status: 500, err});

		console.error('Error fetching from database', err);
	}
}

/**
 * @param {GroupAction} action
 */
export function* saveGroup(action: GroupAction) {
    const session = tabSession.captureGeneration();
    let queueReported = false;
    const reportQueued = (saved: boolean) => {
        if (!queueReported) { queueReported = true; action.onQueued?.(saved); }
    };
    try {
        // console.debug('⮂ GROUP_SAVE saga', action.group?.id);

		if (action.group === undefined || action.group === null) {
			console.error('saveGroup received a null');
			throw new Error('saveGroup received a null');
		}

		if (!(action.group instanceof Group)) {
			action.group = new Group(action.group);
		}
		// OWA-1018: the reducer showed this save at dispatch, stamped after the version this tab holds
		// (OWA-1083), marked pending under this queue key
		const dispatchedAddedOn = action.group.addedOn;

		try {
			// Save to IndexedDB
			ensureOperationId(action.group);
			const addResult = yield call(idb.add, 'group', action.group);
			if (addResult.isErr()) {
				console.error(`⮂ saveGroup: IDB write failed: ${addResult.unwrapErr().message}`);
				reportQueued(false);
				session.assertCurrent();
				// The save never reached the queue: its row returns to the latest accepted version
				yield put(Actions.revertGroupSave(action.group, {sentAddedOn: dispatchedAddedOn}));
				yield put({type: Actions.GROUP_SAVE_FAILURE, group: action.group, status: 500, message: addResult.unwrapErr().message});
				return;
			}
			session.assertCurrent();
			reportQueued(true);

			// Now we can update the user interface: the queued copy replaces the row shown at dispatch
			// (the queue may have stamped it again)
			yield put({type: Actions.GROUP_SAVE, group: action.group, dispatchedAddedOn, queuedAt: performance.now()});

			// A save never deletes autosaved drafts (OWA-958). The editor that wrote a draft
			// removes it when it saves; a draft typed somewhere else may be newer than this copy.
			session.assertCurrent();

			// Let the queue know to process
			yield call(processQueue);
		}
		catch(err) {
			if (session.signal.aborted) { reportQueued(false); return; }
			// OWA-1018: a save that never reached the queue returns its row to the latest accepted version
			if (!queueReported) yield put(Actions.revertGroupSave(action.group, {sentAddedOn: dispatchedAddedOn}));
			reportQueued(false);
			yield put({type: Actions.GROUP_SAVE_FAILURE, group: action.group, status: 500, err});

			console.error('Error saving to database', err);
		}
    } finally { reportQueued(false); }
}

/**
 * TODO: Update for saveMany
 * @param {GroupAction} action
 * @param {Group[]} action.groups
 */
export function* saveManyGroups(action: GroupAction) {
    const session = tabSession.captureGeneration();
    let queueReported = false;
    const reportQueued = (saved: boolean) => {
        if (!queueReported) { queueReported = true; action.onQueued?.(saved); }
    };
    try {
        // console.debug('⮂ GROUP_SAVE_MANY saga', action.groups.map(group => group.id).join(','));

        if (!Array.isArray(action.groups)) {
            console.error('saveManyGroups expected an array, received', action.groups);
            throw new Error('saveManyGroups expected an array');
        }
		// OWA-1018: the reducer showed these saves at dispatch (see saveGroup), under these queue keys
		const dispatchedAddedOns: number[] = action.groups.map(group => group?.addedOn);
		const revertAll = () => action.groups.map((group, i) =>
			put(Actions.revertGroupSave(group, {sentAddedOn: dispatchedAddedOns[i]})));

		try {
			const toSave: Group[] = [];

			// Save each item to IndexedDB
			for(let i = 0; i < action.groups.length; i++) {
				const group = action.groups[i];
				group.persisted = false;
				ensureOperationId(group);
				toSave.push(group);
			}

			const addResult = yield call(idb.add, 'group', toSave);
			if (addResult.isErr()) {
				console.error(`⮂ saveManyGroups: IDB write failed: ${addResult.unwrapErr().message}`);
				reportQueued(false);
				session.assertCurrent();
				// The saves never reached the queue: their rows return to the latest accepted versions
				yield all(revertAll());
				yield put({type: Actions.GROUP_SAVE_MANY_FAILURE, groups: action.groups, status: 500, message: addResult.unwrapErr().message});
				return;
			}
			session.assertCurrent();
			reportQueued(true);

			// Now we can update the user interface: the queued copies replace the rows shown at dispatch
			yield put({type: Actions.GROUP_SAVE_MANY, groups: toSave, dispatchedAddedOns, queuedAt: performance.now()});
			session.assertCurrent();

			// Let the queue know to process
			yield call(processQueue)
		}
		catch(err) {
			if (session.signal.aborted) { reportQueued(false); return; }
			// OWA-1018: saves that never reached the queue return their rows to the latest accepted versions
			if (!queueReported) yield all(revertAll());
			reportQueued(false);
			yield put({type: Actions.GROUP_SAVE_MANY_FAILURE, groups: action.groups, status: 500, err});

			console.error(`Error saving ${action.groups.length} groups to database`, err);
		}
    } finally { reportQueued(false); }
}

/**
 * @param {GroupAction} action
 */
export function* removeGroup(action: GroupAction) {
    const session = tabSession.captureGeneration();
    const userId = getCurrentUserId();
    let queueReported = false;
    const reportQueued = (saved: boolean) => {
        if (!queueReported) { queueReported = true; action.onQueued?.(saved); }
    };
	if (!userId || action.group === undefined || action.group === null || !(action.group instanceof Group)) {
			console.error('removeGroup expected type Group, received', action.group);
			reportQueued(false);
			throw new Error('removeGroup expected type Group');
		}

	try {
		// Mark as deleted. The stamp follows the version this tab holds (the reducer raised the clock, OWA-1083).
		action.group.status = 'D';
		action.group.addedOn = newHRTimestamp();
		action.group.addedBy = userId;

		// Save to IndexedDB
		ensureOperationId(action.group);
		const addResult = yield call(idb.add, 'group', action.group);
		session.assertCurrent();
		if (addResult.isErr()) {
			console.error(`⮂ removeGroup: IDB write failed: ${addResult.unwrapErr().message}`);
			reportQueued(false);
			yield put({type: Actions.GROUP_REMOVE_FAILURE, group: action.group, status: 500, message: addResult.unwrapErr().message});
			return;
		}

		// Now we can update the user interface
		yield put({type: Actions.GROUP_REMOVE, group: action.group});
		session.assertCurrent();

        reportQueued(true);

		// Let the queue know to process
		yield call(processQueue)
	}
	catch(err) {
		if (session.signal.aborted) return;
		yield put({type: Actions.GROUP_REMOVE_FAILURE, group: action.group, status: 500, err});

		console.error('Error deleting from database', err);
	}
    finally { reportQueued(false); }
}

/**
	* Restore the last version of this Group.
	*
	* @param {GroupAction} action
	*/
export function* restoreGroup(action: GroupAction) {
    const session = tabSession.captureGeneration();
    const userId = getCurrentUserId();
    if (!userId) {
        console.error('restoreGroup expected getCurrentUserId() to return a value');
        return;
    }

	if (action.group === undefined || action.group === null) {
        console.error('restoreGroup expected a Group, received null');
        throw new Error('restoreGroup expected a Group, received null');
    }
    else if (!(action.group instanceof Group)) action.group = new Group(action.group); 

	try {
		// Mark as restored. The stamp follows the version this tab holds (the reducer raised the clock, OWA-1083).
		action.group.status = 'U';
		action.group.addedOn = newHRTimestamp();
		action.group.addedBy = userId;
		action.group.restore = true;

		// Save to IndexedDB
		ensureOperationId(action.group);
		const addResult = yield call(idb.add, 'group', action.group);
		session.assertCurrent();
		if (addResult.isErr()) {
			console.error(`⮂ restoreGroup: IDB write failed: ${addResult.unwrapErr().message}`);
			yield put({type: Actions.GROUP_RESTORE_FAILURE, group: action.group, status: 500, message: addResult.unwrapErr().message});
			return;
		}

		// Now we can update the user interface
		yield put({type: Actions.GROUP_RESTORE, group: action.group});
	}
	catch(err) {
		if (session.signal.aborted) return;
		yield put({type: Actions.GROUP_RESTORE_FAILURE, group: action.group, status: 500, err});

		console.error('Error restoring from database', err);
	}
}